import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';
import { PaymentAccount, PaymentAccountFilters } from '@/types/payment-account';

type PaymentAccountDB = Database['public']['Tables']['payment_accounts']['Row'];

interface UsePaymentAccountsReturn {
  accounts: PaymentAccount[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  getCompatibleAccounts: (type: 'income' | 'expense') => PaymentAccount[];
  filters: PaymentAccountFilters;
  setFilters: (filters: PaymentAccountFilters) => void;
}

export const usePaymentAccounts = (initialFilters?: PaymentAccountFilters): UsePaymentAccountsReturn => {
  const [accounts, setAccounts] = useState<PaymentAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<PaymentAccountFilters>(initialFilters || {
    sortBy: 'name',
    sortOrder: 'asc'
  });

  const transformAccountData = useCallback((data: PaymentAccountDB[]): PaymentAccount[] => {
    return (data || []).map((account: PaymentAccountDB) => ({
      id: account.id,
      name: account.name || '',
      description: account.description,
      type: account.type as any,
      bank_name: account.bank_name,
      account_number: account.account_number,
      agency: account.agency,
      initial_balance: Number(account.initial_balance || 0),
      current_balance: Number(account.current_balance || account.balance || 0),
      balance: Number(account.current_balance || account.balance || 0),
      status: account.status as any || 'active',
      color: account.color || '#3b82f6',
      user_id: account.user_id,
      created_at: new Date(account.created_at),
      updated_at: new Date(account.updated_at),
      is_active: account.is_active ?? true,
      is_default: account.is_default ?? false,
      transaction_count: account.transaction_count ?? 0,
      logo_url: account.logo_url ?? undefined,
    }));
  }, []);

  const fetchAccounts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('Usuário não autenticado');
      }

      let query = supabase
        .from('payment_accounts')
        .select('*')
        .eq('user_id', user.id);

      // Apply filters
      if (filters.type) {
        query = query.eq('type', filters.type);
      }

      if (filters.status) {
        query = query.eq('status', filters.status);
      }

      if (filters.bank_name) {
        query = query.ilike('bank_name', `%${filters.bank_name}%`);
      }

      // Apply sorting
      if (filters.sortBy) {
        const ascending = filters.sortOrder === 'asc';
        query = query.order(filters.sortBy, { ascending });
      } else {
        query = query.order('created_at', { ascending: false });
      }

      const { data, error: fetchError } = await query;

      if (fetchError) {
        throw fetchError;
      }

      const transformedAccounts = transformAccountData(data || []);
      setAccounts(transformedAccounts);
    } catch (err) {
      console.error('Erro ao buscar contas de pagamento:', err);
      setError(err instanceof Error ? err.message : 'Erro ao carregar contas');
    } finally {
      setLoading(false);
    }
  }, [filters, transformAccountData]);

  const getCompatibleAccounts = useCallback((transactionType: 'income' | 'expense'): PaymentAccount[] => {
    if (!accounts.length) return [];

    return accounts.filter(account => {
      const accountType = account.type?.toLowerCase();

      if (transactionType === 'income') {
        // Para receitas, permitir contas que podem receber dinheiro
        return ['checking', 'savings', 'digital_wallet', 'pix'].includes(accountType || '');
      } else {
        // Para despesas, permitir contas que podem pagar
        return ['checking', 'savings', 'credit_card', 'digital_wallet', 'pix'].includes(accountType || '');
      }
    });
  }, [accounts]);

  useEffect(() => {
    fetchAccounts();

    // Configurar subscription para atualizações em tempo real
    const channel = supabase
      .channel(`payment_accounts_changes_${Math.random().toString(36).slice(2)}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'payment_accounts'
        },
        (payload) => {
          console.log('Mudança em contas de pagamento detectada:', payload);

          if (payload.eventType === 'INSERT') {
            const newAccount = transformAccountData([payload.new as PaymentAccountDB])[0];
            setAccounts(prev => [newAccount, ...prev]);
          } else if (payload.eventType === 'UPDATE') {
            const updatedAccount = transformAccountData([payload.new as PaymentAccountDB])[0];
            setAccounts(prev =>
              prev.map(account =>
                account.id === updatedAccount.id ? updatedAccount : account
              )
            );
          } else if (payload.eventType === 'DELETE') {
            setAccounts(prev =>
              prev.filter(account => account.id !== payload.old.id)
            );
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchAccounts, transformAccountData]);

  return {
    accounts,
    loading,
    error,
    refetch: fetchAccounts,
    getCompatibleAccounts,
    filters,
    setFilters
  };
};
